/**
 * A CAPA DA FICHA — `T8.17`, a metade que era decisão dele.
 *
 * ── A DECISÃO, E QUEM A TOMOU ───────────────────────────────────────────────────────────────
 *
 * O achado do QA dizia que o cartão da biblioteca não tem miniatura. Mostrar uma exige **tornar
 * pública uma imagem que hoje exige conta**: a arte mora em `biblioteca/{id}/paginas`, e a ficha
 * pública guarda só título, autor, escola e contagem.
 *
 * Havia duas saídas, e a escolha nunca foi minha:
 *
 *   (A) a publicação grava uma capa pequena na ficha, e a tela de publicar pergunta se pode;
 *   (B) um cartão gerado do que já é público — a primeira letra do título sobre a cor da marca.
 *
 * **Ele escolheu a (A), em 28/09.** Este arquivo é ela.
 *
 * ── O QUE ISSO MUDA NO DESENHO DE PRIVACIDADE, DITO SEM RODEIO ──────────────────────────────
 *
 * Um pedaço do desenho da criança passa a ser legível **sem conta**. Era a linha que a divisão
 * ficha/páginas existia para não atravessar, e é por isso que ela só se atravessa com duas travas:
 *
 *   1. **por publicação, e não por produto**: quem publica marca a caixa. Sem a marca, não há capa,
 *      e a ficha sai como sempre saiu;
 *   2. **a caixa nasce desmarcada**. Consentimento que vem marcado não é consentimento — e aqui
 *      quem consente é uma criança sobre o próprio desenho.
 *
 * Desfazer depois exige apagar as capas já gravadas, e isso está dito na tela de publicar em vez de
 * ficar só aqui.
 *
 * ── E POR QUE ELA É MINÚSCULA, COM NÚMERO ───────────────────────────────────────────────────
 *
 * A ficha é UM documento do Firestore, e o teto é 1.048.487 bytes por campo — o mesmo que o `T6.6`
 * encontrou. Uma capa grande recriaria, na ficha, o defeito que as páginas irmãs resolveram.
 *
 * Então a capa é reduzida para caber num orçamento declarado, e a função **recusa** em vez de gravar
 * algo maior: uma ficha que não grava é um cartão sem miniatura, e uma ficha que estoura é uma
 * publicação que falha depois de a criança apertar o botão.
 */

/** O lado maior da capa, em pixels. Cartão de 257 px de largura numa tela comum; 320 cobre retina. */
export const LADO_DA_CAPA = 320;

/**
 * O teto da capa, em bytes de `data:` URI.
 *
 * 90 KiB não é número redondo por acaso: a ficha carrega também título, autor, escola e a publicação,
 * e o teto do campo é ~1 MiB. Com 90 KiB a capa usa menos de 10% do orçamento e sobra folga para o
 * documento crescer sem ninguém revisar esta conta.
 */
export const TETO_DA_CAPA = 90 * 1024;

/** A qualidade inicial. Cai em degraus até caber, e a função desiste em vez de entregar borrão. */
const QUALIDADES = [0.72, 0.6, 0.5, 0.42];

export type Capa = { imagem: string; bytes: number; qualidade: number } | null;

/**
 * O primeiro DESENHO de uma página, no formato que o publicar de fato tem em mãos.
 *
 * ── E O FORMATO IMPORTA, porque eu escrevi a primeira versão sobre o formato errado ─────────
 *
 * O item do EDITOR guarda `imgId` e vai buscar a imagem no acervo da conta; quem publica já não tem
 * isso. Nesta altura do caminho a página está no formato do documento v2 — `camadas` com `elementos`,
 * e a imagem viaja em `refImagem` como `data:` URI —, que é exatamente o que `separarImagens` procura
 * uma linha adiante para transformar em documento irmão.
 *
 * Ler o mesmo campo que `separarImagens` lê não é coincidência: é o que garante que a capa seja um
 * desenho que de fato vai ser publicado, e não um que ficou pelo caminho.
 *
 * "Primeiro quadro" na fala dele é o primeiro DESENHO, e não o primeiro elemento: balão e quadro
 * também são elementos, e uma capa que é o contorno do quadro não diz nada sobre a história.
 */
export function primeiroDesenho(pagina: unknown): string | null {
  const p = pagina as { camadas?: { elementos?: unknown[] }[] } | undefined;
  for (const camada of p?.camadas ?? []) {
    for (const bruto of camada?.elementos ?? []) {
      const el = bruto as { tipo?: unknown; refImagem?: unknown };
      if (el?.tipo !== "imagem") continue;
      const ref = typeof el.refImagem === "string" ? el.refImagem : "";
      if (ref.startsWith("data:image")) return ref;
    }
  }
  return null;
}

/**
 * Reduz a imagem até caber no teto, e devolve `null` quando não cabe.
 *
 * Recebe o `document` por parâmetro para o teste poder exercitá-la — o `canvas` do `jsdom` não
 * rasteriza, e um teste que dependesse do `canvas` real mediria o navegador e não esta função.
 */
export async function capaDaFicha(
  dataUrl: string | null,
  doc: Document = document,
): Promise<Capa> {
  if (!dataUrl || !dataUrl.startsWith("data:image")) return null;

  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = dataUrl;
  });
  if (!img || !img.width || !img.height) return null;

  const escala = Math.min(1, LADO_DA_CAPA / Math.max(img.width, img.height));
  const canvas = doc.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.width * escala));
  canvas.height = Math.max(1, Math.round(img.height * escala));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  for (const qualidade of QUALIDADES) {
    const imagem = canvas.toDataURL("image/jpeg", qualidade);
    if (imagem.length <= TETO_DA_CAPA) return { imagem, bytes: imagem.length, qualidade };
  }
  // Nem no menor degrau coube. Devolver `null` é deliberado: a publicação segue sem capa, que é o
  // comportamento de sempre, em vez de gravar uma ficha que pode estourar o campo.
  return null;
}
